/**
 *
 * Business Rooms & Revenue Status widget wrapper
 * Fetch data & render spinner/error/content depending on state
 *
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import session from 'containers/App/session';

import messages from './messages';
import { defaultLayout } from 'containers/widgets/Widget/propTypes';
import { lySpitOf, y2SpitOf } from 'utils/dates';

import Widget from 'containers/widgets/Widget/Widget';
import BusinessStatusWidget from './Widget';

import { filterDatesInRange, calculateTotals, calculateLastYearVariances } from './utils';

const WidgetWrapper = ({
    layout = defaultLayout,
    filters, // filters for the data to pull
    displayFilters = null,
    currentStats,
    lyFinalStats,
    lySpitStats,
    y2FinalStats,
    y2SpitStats,
    retryFunc,
    dayComparison,
}) => {
    const currency = useSelector(session.selectors.selectCurrency);

    const [data, setData] = useState({});

    const { startDate, endDate } = filters;

    const lySpitStartDate = lySpitOf(startDate, dayComparison);
    const lySpitEndDate = lySpitOf(endDate, dayComparison);
    const y2SpitStartDate = y2SpitOf(startDate, dayComparison);
    const y2SpitEndDate = y2SpitOf(endDate, dayComparison);

    const formatData = () => {
        const filteredData = {
            current: currentStats.data,
            lyFinal: lyFinalStats.data,
            lySpit: lySpitStats.data,
            y2Final: y2FinalStats.data,
            y2Spit: y2SpitStats.data,
        };
        if (filters) {
            filteredData.current = filterDatesInRange(currentStats.data, {
                startDate,
                endDate,
            });
            filteredData.lyFinal = filterDatesInRange(lyFinalStats.data, {
                startDate: lySpitStartDate,
                endDate: lySpitEndDate,
            });
            filteredData.lySpit = filterDatesInRange(lySpitStats.data, {
                startDate: lySpitStartDate,
                endDate: lySpitEndDate,
            });
            filteredData.y2Final = filterDatesInRange(y2FinalStats.data, {
                startDate: y2SpitStartDate,
                endDate: y2SpitEndDate,
            });
            filteredData.y2Spit = filterDatesInRange(y2SpitStats.data, {
                startDate: y2SpitStartDate,
                endDate: y2SpitEndDate,
            });
        }
        const mergedData = {
            current: calculateTotals(filteredData.current),
            lyFinal: calculateTotals(filteredData.lyFinal),
            lySpit: calculateTotals(filteredData.lySpit),
            y2Final: calculateTotals(filteredData.y2Final),
            y2Spit: calculateTotals(filteredData.y2Spit),
        };
        return {
            ...mergedData,
            ...calculateLastYearVariances(mergedData),
        };
    };

    const state = {
        fetching:
            currentStats.fetching ||
            lyFinalStats.fetching ||
            lySpitStats.fetching ||
            y2FinalStats.fetching ||
            y2SpitStats.fetching,
        error: currentStats.error || lyFinalStats.error || lySpitStats.error || y2FinalStats.error || y2SpitStats.error,
        fetched:
            currentStats.fetched &&
            lyFinalStats.fetched &&
            lySpitStats.fetched &&
            y2FinalStats.fetched &&
            y2SpitStats.fetched,
    };

    useEffect(() => {
        if (state.fetched) {
            setData(formatData());
        }
    }, [state.fetched]);

    return (
        <Widget layout={layout} state={state} titleMessage={messages.title} retry={retryFunc}>
            <BusinessStatusWidget data={data} displayFilters={displayFilters} currency={currency} />
        </Widget>
    );
};

export default WidgetWrapper;
